/**
 * @file routes/_app/organic-materials.tsx
 * @description 有機資材審查獨立模組（序77–82）。
 *
 * 功能：
 *  - 列表（序82 排序：申請編號/農產品經營者/驗證註冊碼/資材類型/受理情形/結果/時間）
 *  - 受理登記（序78：申請編號 TG+註冊碼數字+OM+收件日期7碼 自動生成；資材類型 6 類；
 *    品項/製造原料）
 *  - 受理情形三分支（序79：依允用資訊判定[允用來源複選] / 送技術委員會 / 不予受理[原因]）
 *    ＋簽名（導入簽名檔）＋日期
 *  - 審查小組審查（序80：風險判定 低/中/高＋說明 → 審查結果 通過/有條件/不通過＋說明＋簽名）
 *  - ZHSR477-4 審查結果通知單（序81：列印視圖）
 *
 * 位置（序77）：主前台獨立模組（菜單置於風險評估前）；稽核員側亦有入口。
 */

import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  Collapse,
  DatePicker,
  Drawer,
  Form,
  Input,
  Modal,
  Radio,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { FlaskConical, Plus, Printer, Search } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { message } from '@/lib/antd-app';
import { apiClient } from '@/lib/api-client';
import { useAuthStore } from '@/lib/auth-context';
import { userCan } from '@zhcb/shared';
import { ListPageShell, FilterBar, StatusPill } from '@/components/shell';
import ClientSelect from '@/components/common/ClientSelect';
import { OrganicMaterialReferenceLinks } from '@/components/audit-tasks/lab/OrganicMaterialReferenceLinks';

export const Route = createFileRoute('/_app/organic-materials')({
  component: OrganicMaterialsPage,
});

// ─── 常量（序78.2 資材類型 6 大類 / 序79 允用來源 / 不予受理原因） ─────────────

const MATERIAL_CATEGORIES: { key: string; label: string; subs: string[] }[] = [
  {
    key: 'pest',
    label: '病蟲草害防治資材',
    subs: ['病害控制資材', '蟲害控制資材', '草害控制資材', '其他'],
  },
  { key: 'growth', label: '生長調節資材', subs: ['生長調節劑', '儲藏輔助物質', '其他'] },
  {
    key: 'trace',
    label: '微量元素資材',
    subs: ['應依規範檢附土壤或植體診斷結果證明並提供使用計畫'],
  },
  {
    key: 'soil',
    label: '土壤肥力改良資材',
    subs: ['栽培介質', '植物營養', '微生物', '土壤添加劑', '其他'],
  },
  { key: 'seed', label: '一般商業性種子種苗', subs: [] },
  { key: 'other', label: '其他', subs: [] },
];

const ALLOW_SOURCES = [
  '農糧署全球資訊網有機農業商品化資材網路公開品牌',
  '國立中興大學農產品驗證中心有機農業商品化資材遴選一覽表',
  '申請資材於《ZHSR477-5 ZHCERT有機資材允用清單》正面表列',
  '相關主管機關公開資訊中正面表列',
];

const REJECT_REASONS = ['應檢附文件未依規定提供', '不符合有機允用原則'];

const ACCEPTANCE_LABELS: Record<string, string> = {
  direct_allow: '依允用資訊判定',
  committee: '送技術委員會審查',
  rejected: '不予受理',
};

const RISK_LABELS: Record<string, string> = {
  low: '低度風險',
  medium: '中度風險',
  high: '高度風險',
};

const REVIEW_LABELS: Record<string, string> = {
  approved: '審查通過',
  conditional: '有條件通過',
  rejected: '審查不通過',
};

// ─── 型別 ─────────────────────────────────────────────────────────────────────

interface OmApplication {
  id: number;
  applicationNo: string;
  clientId: number;
  clientName: string;
  clientCode: string;
  receivedAt: string | null;
  materialTypes: { categories?: string[]; otherText?: string } | null;
  materialItems: string | null;
  materialIngredients: string | null;
  acceptanceResult: 'direct_allow' | 'committee' | 'rejected' | null;
  acceptanceDetail?: { sources?: string[]; rejectReasons?: string[]; note?: string } | null;
  acceptanceSignedByName?: string | null;
  acceptanceSignDate?: string | null;
  riskLevel: 'low' | 'medium' | 'high' | null;
  riskNote?: string | null;
  reviewResult: 'approved' | 'conditional' | 'rejected' | null;
  reviewNote?: string | null;
  reviewSignedByName?: string | null;
  reviewSignDate?: string | null;
  createdAt: string;
  updatedAt: string;
}

// ─── 主頁面 ───────────────────────────────────────────────────────────────────

function OrganicMaterialsPage() {
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const canManage = user ? userCan(user, 'organic-material-application.manage') : false;

  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [detailId, setDetailId] = useState<number | null>(null);

  const listQuery = useQuery<{ items: OmApplication[]; total: number }>({
    queryKey: ['organic-material-applications', search],
    queryFn: () => {
      const qs = new URLSearchParams({ pageSize: '100' });
      if (search.trim()) qs.set('q', search.trim());
      return apiClient.get(`/api/organic-material-applications?${qs.toString()}`);
    },
  });

  const items = listQuery.data?.items ?? [];

  /** 資材類型摘要（列表展示） */
  const typeSummary = (r: OmApplication) => {
    const keys = r.materialTypes?.categories ?? [];
    const labels = keys
      .map((k) => MATERIAL_CATEGORIES.find((c) => c.key === k)?.label ?? k)
      .filter(Boolean);
    return labels.length ? labels.join('、') : '—';
  };

  // 序82：列表排序——申請編號/農產品經營者/驗證註冊碼/資材類型/受理情形/結果/時間
  const columns: ColumnsType<OmApplication> = [
    {
      title: '申請編號',
      dataIndex: 'applicationNo',
      width: 170,
      render: (v: string, r) => (
        <Button
          type="link"
          size="small"
          style={{ fontFamily: 'monospace', padding: 0 }}
          onClick={() => setDetailId(r.id)}
        >
          {v}
        </Button>
      ),
    },
    { title: '農產品經營者', dataIndex: 'clientName', width: 160, ellipsis: true },
    {
      title: '驗證註冊碼',
      dataIndex: 'clientCode',
      width: 130,
      render: (v: string) => <span style={{ fontFamily: 'monospace' }}>{v}</span>,
    },
    {
      title: '資材類型',
      key: 'types',
      width: 200,
      ellipsis: true,
      render: (_, r) => typeSummary(r),
    },
    {
      title: '受理情形',
      dataIndex: 'acceptanceResult',
      width: 140,
      render: (v: string | null) =>
        v ? (
          <StatusPill
            tone={v === 'rejected' ? 'danger' : v === 'committee' ? 'warning' : 'success'}
            size="sm"
          >
            {ACCEPTANCE_LABELS[v]}
          </StatusPill>
        ) : (
          <Tag>待受理</Tag>
        ),
    },
    {
      title: '結果',
      key: 'result',
      width: 120,
      render: (_, r) => {
        // 依允用資訊判定＝直接通過；送審查會看 reviewResult；不予受理＝終態
        if (r.acceptanceResult === 'direct_allow')
          return (
            <StatusPill tone="success" size="sm">
              允用
            </StatusPill>
          );
        if (r.acceptanceResult === 'rejected')
          return (
            <StatusPill tone="danger" size="sm">
              不予受理
            </StatusPill>
          );
        if (r.reviewResult)
          return (
            <StatusPill
              tone={
                r.reviewResult === 'rejected'
                  ? 'danger'
                  : r.reviewResult === 'conditional'
                    ? 'warning'
                    : 'success'
              }
              size="sm"
            >
              {REVIEW_LABELS[r.reviewResult]}
            </StatusPill>
          );
        if (r.acceptanceResult === 'committee') return <Tag color="processing">審查中</Tag>;
        return <Tag>—</Tag>;
      },
    },
    {
      title: '時間',
      dataIndex: 'receivedAt',
      width: 110,
      render: (v: string | null, r) =>
        (v ?? r.createdAt) ? dayjs(v ?? r.createdAt).format('YYYY-MM-DD') : '—',
    },
  ];

  return (
    <ListPageShell
      title="有機資材審查"
      description="農產品經營者有機資材使用申請之受理、允用判定與技術委員會審查（ZHSR477 系列）。"
      actions={
        canManage && (
          <Button type="primary" icon={<Plus size={14} />} onClick={() => setCreateOpen(true)}>
            受理登記
          </Button>
        )
      }
      filters={
        <FilterBar
          search={
            <Input
              placeholder="搜尋申請編號 / 經營者 / 註冊碼"
              prefix={<Search size={14} />}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              allowClear
            />
          }
        />
      }
    >
      <Collapse
        style={{ marginBottom: 16 }}
        items={[
          {
            key: 'reference-links',
            label: '常用允用資訊查詢連結（可於 系統設置 → 業務字典 — 有機資材查詢連結 維護）',
            children: <OrganicMaterialReferenceLinks />,
          },
        ]}
      />

      <Table<OmApplication>
        rowKey="id"
        columns={columns}
        dataSource={items}
        loading={listQuery.isLoading}
        scroll={{ x: 'max-content' }}
        pagination={{ showTotal: (t) => `共 ${t} 筆` }}
      />

      <CreateDrawer
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(id) => {
          setCreateOpen(false);
          setDetailId(id);
          void qc.invalidateQueries({ queryKey: ['organic-material-applications'] });
        }}
      />

      {detailId != null && (
        <DetailDrawer
          id={detailId}
          canManage={canManage}
          onClose={() => {
            setDetailId(null);
            void qc.invalidateQueries({ queryKey: ['organic-material-applications'] });
          }}
        />
      )}
    </ListPageShell>
  );
}

// ─── 受理登記 Drawer（序78） ──────────────────────────────────────────────────

function CreateDrawer({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (id: number) => void;
}) {
  const [form] = Form.useForm<{
    clientId: number;
    receivedAt?: dayjs.Dayjs;
    categories?: string[];
    otherText?: string;
    materialItems?: string;
    materialIngredients?: string;
  }>();

  const create = useMutation({
    mutationFn: (v: {
      clientId: number;
      receivedAt?: dayjs.Dayjs;
      categories?: string[];
      otherText?: string;
      materialItems?: string;
      materialIngredients?: string;
    }) =>
      apiClient.post<{ item: { id: number } }>('/api/organic-material-applications', {
        clientId: v.clientId,
        receivedAt: v.receivedAt ? v.receivedAt.toISOString() : undefined,
        materialTypes: { categories: v.categories ?? [], otherText: v.otherText },
        materialItems: v.materialItems,
        materialIngredients: v.materialIngredients,
      }),
    onSuccess: (res) => {
      message.success('已受理登記，申請編號已生成');
      form.resetFields();
      onCreated(res.item.id);
    },
    onError: (e: Error) => {
      console.error('[OrganicMaterials] create failed', e);
      message.error(e.message || '登記失敗');
    },
  });

  return (
    <Drawer title="有機資材審查——受理登記" open={open} onClose={onClose} width={560}>
      <Form form={form} layout="vertical" onFinish={(v) => create.mutate(v)}>
        <Form.Item
          name="clientId"
          label="農產品經營者（驗證註冊碼自動關聯）"
          rules={[{ required: true, message: '請選擇經營者' }]}
        >
          <ClientSelect style={{ width: '100%' }} placeholder="從農戶基本資料搜尋勾選…" />
        </Form.Item>
        <Form.Item
          name="receivedAt"
          label="收件日期"
          tooltip="申請編號＝TG+註冊碼數字+OM+收件日期（民國7碼），如 TG1323OM1150410"
          initialValue={dayjs()}
        >
          <DatePicker style={{ width: '100%' }} allowClear={false} />
        </Form.Item>
        <Form.Item name="categories" label="資材類型（可複選）">
          <Checkbox.Group
            style={{ display: 'flex', flexDirection: 'column', gap: 6 }}
            options={MATERIAL_CATEGORIES.map((c) => ({
              value: c.key,
              label: c.subs.length ? `${c.label}（${c.subs.join('/')}）` : c.label,
            }))}
          />
        </Form.Item>
        <Form.Item name="otherText" label="其他說明">
          <Input maxLength={500} placeholder="資材類型為「其他」時說明" />
        </Form.Item>
        <Form.Item name="materialItems" label="有機資材品項">
          <Input.TextArea rows={2} maxLength={2000} />
        </Form.Item>
        <Form.Item name="materialIngredients" label="有機資材製造原料">
          <Input.TextArea rows={2} maxLength={2000} />
        </Form.Item>
        <Button type="primary" htmlType="submit" loading={create.isPending}>
          登記並生成申請編號
        </Button>
      </Form>
    </Drawer>
  );
}

// ─── 詳情 Drawer（序79 受理 / 序80 審查 / 序81 通知單） ────────────────────────

function DetailDrawer({
  id,
  canManage,
  onClose,
}: {
  id: number;
  canManage: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const { data } = useQuery<{ item: OmApplication }>({
    queryKey: ['organic-material-application', id],
    queryFn: () => apiClient.get(`/api/organic-material-applications/${id}`),
  });
  const item = data?.item ?? null;

  // 受理表單狀態
  const [acceptResult, setAcceptResult] = useState<'direct_allow' | 'committee' | 'rejected'>();
  const [allowSources, setAllowSources] = useState<string[]>([]);
  const [rejectReasons, setRejectReasons] = useState<string[]>([]);
  const [acceptNote, setAcceptNote] = useState('');
  const [signatureId, setSignatureId] = useState<number | null>(null);
  // 審查小組表單狀態
  const [riskLevel, setRiskLevel] = useState<'low' | 'medium' | 'high'>();
  const [riskNote, setRiskNote] = useState('');
  const [reviewResult, setReviewResult] = useState<'approved' | 'conditional' | 'rejected'>();
  const [reviewNote, setReviewNote] = useState('');
  // 通知單
  const [noticeOpen, setNoticeOpen] = useState(false);

  const importSignature = useMutation({
    mutationFn: async () => apiClient.get<{ fileId: number | null }>('/api/auth/me/signature'),
    onSuccess: (res) => {
      if (res.fileId) {
        setSignatureId(res.fileId);
        message.success('已導入我的簽名檔');
      } else {
        message.warning('尚未設定簽名檔，請至員工檔案上傳簽名');
      }
    },
    onError: (e: Error) => message.error(e.message || '導入簽名檔失敗'),
  });

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['organic-material-application', id] });
    void qc.invalidateQueries({ queryKey: ['organic-material-applications'] });
  };

  const acceptance = useMutation({
    mutationFn: () =>
      apiClient.post(`/api/organic-material-applications/${id}/acceptance`, {
        result: acceptResult,
        detail: {
          sources: acceptResult === 'direct_allow' ? allowSources : undefined,
          rejectReasons: acceptResult === 'rejected' ? rejectReasons : undefined,
          note: acceptNote.trim() || undefined,
        },
        signatureFileId: signatureId ?? undefined,
      }),
    onSuccess: () => {
      message.success(acceptResult === 'committee' ? '已提交有機資材審查小組' : '受理情形已記錄');
      invalidate();
    },
    onError: (e: Error) => {
      console.error('[OrganicMaterials] acceptance failed', e);
      message.error(e.message || '提交失敗');
    },
  });

  const review = useMutation({
    mutationFn: () =>
      apiClient.post(`/api/organic-material-applications/${id}/review`, {
        riskLevel,
        riskNote: riskNote.trim() || undefined,
        reviewResult,
        reviewNote: reviewNote.trim() || undefined,
        signatureFileId: signatureId ?? undefined,
      }),
    onSuccess: () => {
      message.success('審查結果已記錄，可生成 ZHSR477-4 通知單');
      invalidate();
    },
    onError: (e: Error) => {
      console.error('[OrganicMaterials] review failed', e);
      message.error(e.message || '提交失敗');
    },
  });

  if (!item) {
    return <Drawer open onClose={onClose} width={640} title="有機資材審查" />;
  }

  const finished =
    item.acceptanceResult === 'direct_allow' ||
    item.acceptanceResult === 'rejected' ||
    item.reviewResult != null;

  return (
    <Drawer
      open
      onClose={onClose}
      width={640}
      title={
        <Space>
          <FlaskConical size={16} />
          <span style={{ fontFamily: 'monospace' }}>{item.applicationNo}</span>
        </Space>
      }
      extra={
        finished && (
          <Button icon={<Printer size={14} />} onClick={() => setNoticeOpen(true)}>
            ZHSR477-4 通知單
          </Button>
        )
      }
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        {/* 基本資料 */}
        <div>
          <Typography.Text strong>基本資料</Typography.Text>
          <div style={{ fontSize: 13, marginTop: 6, lineHeight: 1.9 }}>
            <div>
              農產品經營者：{item.clientName}（{item.clientCode}）
            </div>
            <div>
              收件日期：{item.receivedAt ? dayjs(item.receivedAt).format('YYYY-MM-DD') : '—'}
            </div>
            <div>
              資材類型：
              {(item.materialTypes?.categories ?? [])
                .map((k) => MATERIAL_CATEGORIES.find((c) => c.key === k)?.label ?? k)
                .join('、') || '—'}
              {item.materialTypes?.otherText ? `（其他：${item.materialTypes.otherText}）` : ''}
            </div>
            <div>有機資材品項：{item.materialItems || '—'}</div>
            <div>製造原料：{item.materialIngredients || '—'}</div>
          </div>
        </div>

        {/* 受理情形（序79） */}
        <div>
          <Typography.Text strong>受理情形</Typography.Text>
          {item.acceptanceResult ? (
            <div style={{ fontSize: 13, marginTop: 6, lineHeight: 1.9 }}>
              <div>
                <Tag
                  color={
                    item.acceptanceResult === 'rejected'
                      ? 'error'
                      : item.acceptanceResult === 'committee'
                        ? 'processing'
                        : 'success'
                  }
                >
                  {ACCEPTANCE_LABELS[item.acceptanceResult]}
                </Tag>
              </div>
              {!!item.acceptanceDetail?.sources?.length && (
                <div>允用資訊來源：{item.acceptanceDetail.sources.join('；')}</div>
              )}
              {!!item.acceptanceDetail?.rejectReasons?.length && (
                <div>不予受理原因：{item.acceptanceDetail.rejectReasons.join('；')}</div>
              )}
              {item.acceptanceDetail?.note && <div>說明：{item.acceptanceDetail.note}</div>}
              <div>
                簽名：{item.acceptanceSignedByName ?? '—'}・日期：{item.acceptanceSignDate ?? '—'}
              </div>
            </div>
          ) : canManage ? (
            <Space direction="vertical" size={8} style={{ width: '100%', marginTop: 6 }}>
              <Radio.Group
                value={acceptResult}
                onChange={(e) => setAcceptResult(e.target.value as typeof acceptResult)}
              >
                <Space direction="vertical">
                  <Radio value="direct_allow">不需送技術委員會審查，依既有允用資訊判定</Radio>
                  <Radio value="committee">需送技術委員會召開有機資材審查會議</Radio>
                  <Radio value="rejected">不予受理</Radio>
                </Space>
              </Radio.Group>
              {acceptResult === 'direct_allow' && (
                <Checkbox.Group
                  style={{ display: 'flex', flexDirection: 'column', gap: 4 }}
                  value={allowSources}
                  onChange={(v) => setAllowSources(v)}
                  options={ALLOW_SOURCES.map((s) => ({ label: s, value: s }))}
                />
              )}
              {acceptResult === 'rejected' && (
                <Checkbox.Group
                  style={{ display: 'flex', flexDirection: 'column', gap: 4 }}
                  value={rejectReasons}
                  onChange={(v) => setRejectReasons(v)}
                  options={REJECT_REASONS.map((s) => ({ label: s, value: s }))}
                />
              )}
              {acceptResult === 'committee' && (
                <Alert
                  type="info"
                  showIcon
                  message="確認經營者提出之有機資材尚未於主管機關公開資訊中正面表列，且無可替代性有機資材；提交後由審查小組（5 位）進行風險判定與審查。"
                />
              )}
              <Input.TextArea
                rows={2}
                value={acceptNote}
                onChange={(e) => setAcceptNote(e.target.value)}
                placeholder="說明"
                maxLength={2000}
              />
              <Space>
                <Button
                  type="primary"
                  ghost
                  size="small"
                  loading={importSignature.isPending}
                  onClick={() => importSignature.mutate()}
                >
                  導入簽名檔
                </Button>
                {signatureId && <Tag color="success">已簽名</Tag>}
                <Button
                  type="primary"
                  disabled={!acceptResult}
                  loading={acceptance.isPending}
                  onClick={() => acceptance.mutate()}
                >
                  提交受理情形
                </Button>
              </Space>
            </Space>
          ) : (
            <div style={{ fontSize: 13, marginTop: 6 }}>待受理</div>
          )}
        </div>

        {/* 審查小組審查（序80，僅送技術委員會後） */}
        {item.acceptanceResult === 'committee' && (
          <div>
            <Typography.Text strong>有機資材審查小組審查（共 5 位）</Typography.Text>
            {item.reviewResult ? (
              <div style={{ fontSize: 13, marginTop: 6, lineHeight: 1.9 }}>
                <div>
                  風險判定：<Tag>{item.riskLevel ? RISK_LABELS[item.riskLevel] : '—'}</Tag>
                  {item.riskNote && <span>（{item.riskNote}）</span>}
                </div>
                <div>
                  審查結果：
                  <Tag
                    color={
                      item.reviewResult === 'rejected'
                        ? 'error'
                        : item.reviewResult === 'conditional'
                          ? 'warning'
                          : 'success'
                    }
                  >
                    {REVIEW_LABELS[item.reviewResult]}
                  </Tag>
                  {item.reviewNote && <span>（{item.reviewNote}）</span>}
                </div>
                <div>
                  簽名：{item.reviewSignedByName ?? '—'}・日期：{item.reviewSignDate ?? '—'}
                </div>
              </div>
            ) : canManage ? (
              <Space direction="vertical" size={8} style={{ width: '100%', marginTop: 6 }}>
                <div>
                  <span style={{ fontSize: 13 }}>1、有機資材風險判定：</span>
                  <Radio.Group
                    value={riskLevel}
                    onChange={(e) => setRiskLevel(e.target.value as typeof riskLevel)}
                  >
                    <Radio value="low">低度風險</Radio>
                    <Radio value="medium">中度風險</Radio>
                    <Radio value="high">高度風險</Radio>
                  </Radio.Group>
                </div>
                <Input.TextArea
                  rows={2}
                  value={riskNote}
                  onChange={(e) => setRiskNote(e.target.value)}
                  placeholder="風險判定說明"
                  maxLength={2000}
                />
                <div>
                  <span style={{ fontSize: 13 }}>2、依風險結果判定：</span>
                  <Radio.Group
                    value={reviewResult}
                    onChange={(e) => setReviewResult(e.target.value as typeof reviewResult)}
                  >
                    <Radio value="approved">審查通過</Radio>
                    <Radio value="conditional">審查結果有條件通過</Radio>
                    <Radio value="rejected">審查不通過</Radio>
                  </Radio.Group>
                </div>
                <Input.TextArea
                  rows={2}
                  value={reviewNote}
                  onChange={(e) => setReviewNote(e.target.value)}
                  placeholder="審查結果說明"
                  maxLength={2000}
                />
                <Space>
                  <Button
                    type="primary"
                    ghost
                    size="small"
                    loading={importSignature.isPending}
                    onClick={() => importSignature.mutate()}
                  >
                    導入簽名檔
                  </Button>
                  {signatureId && <Tag color="success">已簽名</Tag>}
                  <Button
                    type="primary"
                    disabled={!riskLevel || !reviewResult}
                    loading={review.isPending}
                    onClick={() => review.mutate()}
                  >
                    提交審查結果
                  </Button>
                </Space>
              </Space>
            ) : (
              <div style={{ fontSize: 13, marginTop: 6 }}>審查中</div>
            )}
          </div>
        )}
      </Space>

      {/* 序81：ZHSR477-4 審查結果通知單（列印視圖） */}
      <Modal
        title="ZHSR477-4 有機資材審查結果通知單"
        open={noticeOpen}
        onCancel={() => setNoticeOpen(false)}
        footer={
          <Button type="primary" icon={<Printer size={14} />} onClick={() => window.print()}>
            列印
          </Button>
        }
        width={720}
      >
        <div className="print-area" style={{ lineHeight: 2, fontSize: 14 }}>
          <h3 style={{ textAlign: 'center' }}>ZHCERT 有機資材審查結果通知單（ZHSR477-4）</h3>
          <p>申請編號：{item.applicationNo}</p>
          <p>
            農產品經營者：{item.clientName}（驗證註冊碼 {item.clientCode}）
          </p>
          <p>
            資材類型：
            {(item.materialTypes?.categories ?? [])
              .map((k) => MATERIAL_CATEGORIES.find((c) => c.key === k)?.label ?? k)
              .join('、') || '—'}
          </p>
          <p>有機資材品項：{item.materialItems || '—'}</p>
          <p>
            受理情形：{item.acceptanceResult ? ACCEPTANCE_LABELS[item.acceptanceResult] : '—'}
            {item.acceptanceDetail?.note ? `（${item.acceptanceDetail.note}）` : ''}
          </p>
          {item.acceptanceResult === 'committee' && (
            <>
              <p>風險判定：{item.riskLevel ? RISK_LABELS[item.riskLevel] : '—'}</p>
              <p>
                審查結果：{item.reviewResult ? REVIEW_LABELS[item.reviewResult] : '—'}
                {item.reviewNote ? `（${item.reviewNote}）` : ''}
              </p>
            </>
          )}
          <p>
            審查人員：{item.reviewSignedByName ?? item.acceptanceSignedByName ?? '—'}・日期：
            {item.reviewSignDate ?? item.acceptanceSignDate ?? '—'}
          </p>
        </div>
      </Modal>
    </Drawer>
  );
}
